import React, { useState, useEffect } from 'react';
import AddContactModal from './AddContactModal';
import AddGroupModal from './AddGroupModal';
import { getContactsByUser, deletecontact, getContactGroupByUser, deletecontactgroup } from '../components/DbServices';
import Layout from '../layout/Layout';
import PageTransition from '../components/PageTransition';
import { getStoredUser } from '../utilities/encryption';
import BulkUploadContactsModal from './BulkUploadContactsModal';
import Swal from 'sweetalert2';

export default function ContactList() {
    const user = getStoredUser();
    const userId = user?.data?.id;

    const [activeTab, setActiveTab] = useState('contacts');
    const [contacts, setContacts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBulkModal, setShowBulkModal] = useState(false);

    
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [showModal, setShowModal] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const fetchData = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const contactRes = await getContactsByUser({ id: userId });
            setContacts(contactRes.data || []);

            const groupRes = await getContactGroupByUser(userId);
            setGroups(groupRes.data || []);
        } catch (err) {
            console.error("Failed to fetch data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        setCurrentPage(1);
    }, [userId, activeTab]);

    const filteredData = (activeTab === 'contacts' ? contacts : groups).filter(item => {
        const name = activeTab === 'contacts' ? item.name : item.groupname;
        const extra = activeTab === 'contacts' ? item.number : '';
        return (
            name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            extra?.includes(searchTerm)
        );
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handleDeleteContact = async (id) => {
        if (!window.confirm("Delete contact?")) return;
        await deletecontact({ id });
        fetchData();
    };

    const handleDeleteGroup = async (id) => {
        if (!window.confirm("Delete this group?")) return;
        await deletecontactgroup({ id });
        fetchData();
    };

    return (
        <Layout>
            <PageTransition>
                <div className="container-fluid py-4">
                    
                    {/* HEADER SECTION */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                        <div>
                            <h3 className="fw-bold mb-1"><span className="color-theme">Audience Hub</span> 👥</h3>
                            <p className="text-muted small fw-medium">Manage your {activeTab} and broadcast lists.</p>
                        </div>
                        <button 
                            className="btn btn-primary rounded-pill px-4 fw-bold shadow border-0"
                            style={{ background: 'linear-gradient(45deg, #4f46e5, #9333ea)' }}
                            onClick={() => {
                                if(activeTab === 'contacts') { setSelectedContact(null); setShowModal(true); }
                                else { setSelectedGroup(null); setShowGroupModal(true); }
                            }}
                        >
                            <i className={`bi bi-${activeTab === 'contacts' ? 'person-plus' : 'grid-plus'} me-2`}></i>
                            Add {activeTab === 'contacts' ? 'Contact' : 'Group'}
                        </button>
                    </div>

                    {/* SEARCH & NAVIGATION TAB BAR */}
                    <div className="row mb-4 g-3 align-items-center">
                        <div className="col-md-6">
                            <div className="bg-light p-1 rounded-pill d-inline-flex shadow-sm border">
                                <button 
                                    className={`btn rounded-pill px-4 py-2 fw-bold transition-all ${activeTab === 'contacts' ? 'btn-primary shadow border-0' : 'btn-light border-0 text-muted'}`}
                                    onClick={() => setActiveTab('contacts')}
                                    style={activeTab === 'contacts' ? { background: '#4f46e5' } : {}}
                                >
                                    Contacts
                                </button>
                                <button
            className="btn btn-outline-primary rounded-pill px-4 fw-bold shadow-sm"
            onClick={() => setShowBulkModal(true)}
        >
            <i className="bi bi-upload me-2"></i>
            Bulk Upload
        </button>
                                <button 
                                    className={`btn rounded-pill px-4 py-2 fw-bold transition-all ${activeTab === 'groups' ? 'btn-primary shadow border-0' : 'btn-light border-0 text-muted'}`}
                                    onClick={() => setActiveTab('groups')}
                                    style={activeTab === 'groups' ? { background: '#4f46e5' } : {}}
                                >
                                    Groups
                                </button>
                            </div>
                        </div>
                        <div className="col-md-6 text-end">
                            <div className="input-group shadow-sm rounded-pill overflow-hidden border bg-white px-3 py-1">
                                <span className="bg-transparent border-0 text-muted py-2"><i className="bi bi-search"></i></span>
                                <input 
                                    type="text" 
                                    className="form-control border-0 bg-transparent shadow-none" 
                                    placeholder={`Search in ${activeTab}...`} 
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* MAIN TABLE DATA */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 custom-admin-table">
                                <thead style={{ backgroundColor: '#f8fafc' }}>
                                    <tr className="text-muted small text-uppercase fw-bold">
                                        <th className="ps-4 py-4">Identity</th>
                                        <th className="py-4 text-center">{activeTab === 'contacts' ? 'Phone Number' : 'Member Count'}</th>
                                        <th className="py-4 text-center">Status</th>
                                        <th className="pe-4 py-4 text-end">Management</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="4" className="text-center py-5 text-muted small"><div className="spinner-border text-primary border-2 mb-2"></div><br/>Syncing Audience...</td></tr>
                                    ) : currentItems.length === 0 ? (
                                        <tr><td colSpan="4" className="text-center py-5 text-muted fw-medium">No records found in {activeTab}.</td></tr>
                                    ) : (
                                        currentItems.map(item => (
                                            <tr key={item.id} className="border-bottom border-light">
                                                <td className="ps-4 py-3">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className={`rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white`} 
                                                             style={{ width: '40px', height: '40px', background: activeTab === 'contacts' ? 'linear-gradient(45deg, #0ea5e9, #2563eb)' : 'linear-gradient(45deg, #f59e0b, #d97706)' }}>
                                                            <i className={`bi bi-${activeTab === 'contacts' ? 'person' : 'people'}`}></i>
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark small mb-0">{activeTab === 'contacts' ? item.name : item.groupname}</div>
                                                            <div className="text-muted x-small">ID: {item.id}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {activeTab === 'contacts' ? (
                                                        <code className="text-indigo bg-indigo-subtle px-3 py-1 rounded-pill fw-bold" style={{ fontSize: '12px' }}>
                                                            +{item.number}
                                                        </code>
                                                    ) : (
                                                        <span className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-bold">
                                                            {(() => {
                                                                try { return JSON.parse(item.contacts).length; } catch (e) { return 0; }
                                                            })()} Members
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge rounded-pill fw-bold ${Number(item.status) === 1 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'} px-3 py-2`} style={{ fontSize: '10px' }}>
                                                        <span className={`pulse-dot me-2 ${Number(item.status) === 1 ? 'bg-success' : 'bg-danger'}`}></span>
                                                        {Number(item.status) === 1 ? 'ACTIVE' : 'INACTIVE'}
                                                    </span>
                                                </td>
                                                <td className="pe-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button 
                                                            className="btn btn-light-indigo btn-sm border-0 rounded-3"
                                                            onClick={() => activeTab === 'contacts' ? (setSelectedContact(item), setShowModal(true)) : (setSelectedGroup(item), setShowGroupModal(true))}
                                                        >
                                                            <i className="bi bi-pencil-square"></i>
                                                        </button>
                                                        <button 
                                                            className="btn btn-light-danger btn-sm border-0 rounded-3"
                                                            onClick={() => activeTab === 'contacts' ? handleDeleteContact(item.id) : handleDeleteGroup(item.id)}
                                                        >
                                                            <i className="bi bi-trash3"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* PAGINATION */}
                    {!loading && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4 px-2">
                            <p className="text-muted small mb-0">Page <strong>{currentPage}</strong> of {totalPages}</p>
                            <nav>
                                <ul className="pagination pagination-sm mb-0 gap-1">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link border-0 rounded-pill px-3" onClick={() => setCurrentPage(prev => prev - 1)}>Prev</button>
                                    </li>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                            <button className="page-link border-0 rounded-pill px-3 mx-1" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button className="page-link border-0 rounded-pill px-3" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}

                    {showModal && <AddContactModal userId={userId} editData={selectedContact} onClose={() => setShowModal(false)} onSaved={() => { fetchData(); setShowModal(false); }} />}
                    {showGroupModal && <AddGroupModal userId={userId} contactsList={contacts} editData={selectedGroup} onClose={() => setShowGroupModal(false)} onSaved={() => { fetchData(); setShowGroupModal(false); }} />}
                    {showBulkModal && (
    <BulkUploadContactsModal
        userId={userId}
        onClose={() => setShowBulkModal(false)}
        onSaved={() => {
            fetchData();
            setShowBulkModal(false);
        }}
    />
)}

                
                </div>
            </PageTransition>
        </Layout>
    );
}